import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getDomains,
    getPriceCascades,
    getAllPriceCascades,
    updatePriceCascade,
    deletePriceCascade,
    getCascadeSteps,
    getCascadeGranulations,
    createCascadeGranulation,
    updateCascadeGranulation,
    deleteCascadeGranulation,
    getGranulation,
} from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
    Plus, Pencil, Trash2, Loader2, Search,
    ChevronDown, ChevronRight, Check, Layers, ArrowDownUp, Timer,
    ArrowRight, CircleDot
} from 'lucide-react';
import type { PriceCascade, UpdatePriceCascadeInput, CascadeGranulation, CreateCascadeGranulationInput } from '@/types/price-rules';
import { Checkbox } from '@/components/ui/checkbox';
import type { Domain } from '@/types/domain';
import CreateCascadeModal from './CreateCascadeModal';
import EditCascadeModal from './EditCascadeModal';
import type { CreateCascadePrefill } from './CreateCascadeModal';

// ========== AGRUPAMENTO P1 -> P2 (view helpers) ==========

type PostGroup = { key: string; slug: string | null; id_post_wp: string | null; cascades: PriceCascade[] };
type CascadeRow = { key: string; origins: PostGroup[]; destSlug: string | null; destGroup: PostGroup | null };
type DomainGroup = { domainId: number; domain: string; cascades: PriceCascade[]; rows: CascadeRow[]; postGroups: Map<string, PostGroup> };

const normalizeSlug = (s: string | null | undefined) => (s || '').replace(/^\/+/, '').replace(/\/+$/, '');

const stepsOf = (c: PriceCascade) => c.granulation_steps || c.steps || [];
const stepsSummary = (steps: number[]) =>
    steps.length ? `${steps.length} steps · $${steps[0]} → $${steps[steps.length - 1]}` : '—';

const AD_UNIT_DOT: Record<string, string> = {
    rewarded: 'bg-violet-500',
    interstitial: 'bg-teal-500',
    topo: 'bg-blue-500',
};
const adUnitDot = (u: string) => AD_UNIT_DOT[u.toLowerCase()] || 'bg-gray-400';

const COMPACT_GRID = 'grid grid-cols-[130px_minmax(170px,1fr)_110px_100px_64px_96px_64px] items-center gap-2';

const isActive = (c: PriceCascade) => c.status === 'active';

/**
 * Barra de aplicacao em massa: filtra por ad unit e aplica Granulacao P1,
 * Granulacao P2 ou Modo de Blocos em todas as regras do escopo de uma vez.
 * Com `scopes`, mostra tambem um seletor de escopo (origens/destinos/site todo).
 */
function BulkApplyBar({ list, scopes, granulations, applying, onApply, label = 'Aplicar a todos:' }: {
    list?: PriceCascade[];
    scopes?: { label: string; list: PriceCascade[] }[];
    granulations?: CascadeGranulation[];
    applying: boolean;
    onApply: (targets: PriceCascade[], data: UpdatePriceCascadeInput, desc: string) => void;
    label?: string;
}) {
    const [scopeIdx, setScopeIdx] = useState(0);
    const [adUnit, setAdUnit] = useState('all');
    const [target, setTarget] = useState<'p1' | 'p2' | 'blocks'>('p1');

    const base = scopes ? (scopes[scopeIdx]?.list || []) : (list || []);
    const adUnits = Array.from(new Set(base.map(c => c.ad_unit)));
    const targets = adUnit === 'all' ? base : base.filter(c => c.ad_unit === adUnit);

    const handleValue = (v: string) => {
        if (target === 'p1') {
            onApply(targets, { granulation_id: v === 'default' ? null : Number(v) }, 'Granulacao');
        } else if (target === 'p2') {
            onApply(targets, { granulation_id_p2: v === 'inherit' ? null : Number(v) }, 'Granulacao pos-redirect');
        } else {
            onApply(targets, { block_mode: v as 'standard' | 'cascade_blocks' }, 'Modo de blocos');
        }
    };

    return (
        <div className="flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
            <span className="text-[11px] text-gray-400 whitespace-nowrap">{label}</span>
            {applying && <Loader2 className="h-3.5 w-3.5 animate-spin text-purple-500" />}
            {scopes && (
                <Select value={String(scopeIdx)} onValueChange={(v) => { setScopeIdx(Number(v)); setAdUnit('all'); }} disabled={applying}>
                    <SelectTrigger className="h-7 w-[150px] text-xs bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {scopes.map((s, i) => (
                            <SelectItem key={s.label} value={String(i)}>{s.label} ({s.list.length})</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
            <Select value={adUnit} onValueChange={setAdUnit} disabled={applying}>
                <SelectTrigger className="h-7 w-[130px] text-xs bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">Todas ad units</SelectItem>
                    {adUnits.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={target} onValueChange={(v) => setTarget(v as 'p1' | 'p2' | 'blocks')} disabled={applying}>
                <SelectTrigger className="h-7 w-[140px] text-xs bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="p1">Granulacao</SelectItem>
                    <SelectItem value="p2">Pos-redirect (P2)</SelectItem>
                    <SelectItem value="blocks">Modo de blocos</SelectItem>
                </SelectContent>
            </Select>
            <Select value="" onValueChange={handleValue} disabled={applying || targets.length === 0}>
                <SelectTrigger className="h-7 w-[180px] text-xs bg-white"><SelectValue placeholder="Escolher valor..." /></SelectTrigger>
                <SelectContent>
                    {target === 'p1' && (
                        <>
                            <SelectItem value="default">Padrao</SelectItem>
                            {granulations?.map(g => (
                                <SelectItem key={g.id} value={String(g.id)}>{g.name} ({g.steps.length} steps)</SelectItem>
                            ))}
                        </>
                    )}
                    {target === 'p2' && (
                        <>
                            <SelectItem value="inherit">— usar a de P1 —</SelectItem>
                            {granulations?.map(g => (
                                <SelectItem key={g.id} value={String(g.id)}>{g.name} ({g.steps.length} steps)</SelectItem>
                            ))}
                        </>
                    )}
                    {target === 'blocks' && (
                        <>
                            <SelectItem value="standard">Bloco Padrao</SelectItem>
                            <SelectItem value="cascade_blocks">Cascade Blocks</SelectItem>
                        </>
                    )}
                </SelectContent>
            </Select>
            <span className="text-[10px] text-gray-400 whitespace-nowrap">
                {targets.length} regra{targets.length !== 1 ? 's' : ''}
            </span>
        </div>
    );
}

export default function PriceCascades() {
    const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
    const [domainOpen, setDomainOpen] = useState(false);
    const [domainSearch, setDomainSearch] = useState('');
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createPrefill, setCreatePrefill] = useState<CreateCascadePrefill>({});
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selected, setSelected] = useState<PriceCascade | null>(null);
    const [collapsedDomains, setCollapsedDomains] = useState<Set<number>>(new Set());
    const [expandedPanels, setExpandedPanels] = useState<Record<string, string>>({});
    const [postModal, setPostModal] = useState<{ domainId: number; postKey: string | null; slug: string | null } | null>(null);
    const [bulkApplying, setBulkApplying] = useState(false);
    const [isGranulationListOpen, setIsGranulationListOpen] = useState(false);
    const [expandedGranulations, setExpandedGranulations] = useState<Set<number>>(new Set());
    const [isGranulationOpen, setIsGranulationOpen] = useState(false);
    const [editingGranulation, setEditingGranulation] = useState<CascadeGranulation | null>(null);
    const [granulationForm, setGranulationForm] = useState<{ name: string; steps: number[] }>({ name: '', steps: [] });
    const [stepsPopoverOpen, setStepsPopoverOpen] = useState(false);

    const { toast } = useToast();
    const queryClient = useQueryClient();

    // ========== QUERIES ==========

    const { data: domains } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    const { data: cascades, isLoading } = useQuery({
        queryKey: ['price-cascades', selectedDomainId],
        queryFn: () => selectedDomainId ? getPriceCascades(selectedDomainId) : getAllPriceCascades(),
    });

    const { data: cascadeStepsConfig } = useQuery({
        queryKey: ['cascade-steps'],
        queryFn: getCascadeSteps,
    });

    const { data: granulations } = useQuery({
        queryKey: ['cascade-granulations'],
        queryFn: getCascadeGranulations,
    });

    const { data: priceGranulation } = useQuery({
        queryKey: ['price-granulation'],
        queryFn: getGranulation,
    });

    // Available price values from GAM granulation (reversed for high→low display)
    const availablePrices = useMemo(() => {
        return priceGranulation?.values ? [...priceGranulation.values].reverse() : [];
    }, [priceGranulation]);

    // ========== MUTATIONS ==========

    // Edicao rapida inline (dropdown de step) — nao fecha modais
    const quickUpdateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdatePriceCascadeInput }) => updatePriceCascade(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['price-cascades'] });
            toast({ title: 'Cascade atualizado' });
        },
        onError: (err: any) => {
            toast({ title: 'Erro ao atualizar cascade', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        },
    });

    /**
     * Liga/desliga uma cascata sem excluir a configuracao.
     * Inativa continua no banco e na listagem, mas sai do ad{N}.js na
     * regeneracao que a propria API dispara depois do update.
     */
    const toggleStatusMutation = useMutation({
        mutationFn: ({ id, status }: { id: number; status: 'active' | 'inactive'; adUnit: string }) =>
            updatePriceCascade(id, { status }),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: ['price-cascades'] });
            toast({
                title: vars.status === 'active'
                    ? `Cascata ${vars.adUnit} ativada`
                    : `Cascata ${vars.adUnit} desativada`,
                description: vars.status === 'active'
                    ? 'Volta a valer no proximo carregamento do site.'
                    : 'Deixa de ser aplicada no proximo carregamento do site.',
            });
        },
        onError: (err: any) => {
            toast({ title: 'Erro ao mudar o status da cascata', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        },
    });

    const isToggling = (id: number) =>
        toggleStatusMutation.isPending && toggleStatusMutation.variables?.id === id;

    const applyBulk = async (targets: PriceCascade[], data: UpdatePriceCascadeInput, desc: string) => {
        if (!targets.length) {
            toast({ title: 'Nenhuma regra corresponde ao filtro', variant: 'destructive' });
            return;
        }
        setBulkApplying(true);
        try {
            await Promise.all(targets.map(c => updatePriceCascade(c.id, data)));
            queryClient.invalidateQueries({ queryKey: ['price-cascades'] });
            toast({ title: `${desc} aplicado a ${targets.length} regra${targets.length !== 1 ? 's' : ''}` });
        } catch (err: any) {
            toast({ title: 'Erro ao aplicar em massa', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        } finally {
            setBulkApplying(false);
        }
    };

    const deleteMutation = useMutation({
        mutationFn: deletePriceCascade,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['price-cascades'] });
            toast({ title: 'Cascade removido com sucesso' });
            setIsDeleteOpen(false);
            setSelected(null);
        },
        onError: (err: any) => {
            toast({ title: 'Erro ao remover cascade', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        },
    });

    const createGranulationMutation = useMutation({
        mutationFn: createCascadeGranulation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cascade-granulations'] });
            toast({ title: 'Granulacao criada com sucesso' });
            setIsGranulationOpen(false);
            setEditingGranulation(null);
        },
        onError: (err: any) => {
            toast({ title: 'Erro ao criar granulacao', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        },
    });

    const updateGranulationMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<CreateCascadeGranulationInput> }) => updateCascadeGranulation(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cascade-granulations'] });
            queryClient.invalidateQueries({ queryKey: ['price-cascades'] });
            toast({ title: 'Granulacao atualizada com sucesso' });
            setIsGranulationOpen(false);
            setEditingGranulation(null);
        },
        onError: (err: any) => {
            toast({ title: 'Erro ao atualizar granulacao', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        },
    });

    const deleteGranulationMutation = useMutation({
        mutationFn: deleteCascadeGranulation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cascade-granulations'] });
            queryClient.invalidateQueries({ queryKey: ['price-cascades'] });
            toast({ title: 'Granulacao removida com sucesso' });
        },
        onError: (err: any) => {
            toast({ title: 'Erro ao remover granulacao', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        },
    });

    // ========== HELPERS ==========

    const filteredDomains = domains?.filter((d: Domain) =>
        d.domain.toLowerCase().includes(domainSearch.toLowerCase())
    ) || [];

    const selectedDomain = domains?.find((d: Domain) => d.id === selectedDomainId);
    const getFaviconUrl = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    const getDomainName = (domainId: number) => domains?.find((d: Domain) => d.id === domainId)?.domain || `ID: ${domainId}`;

    // O prefill fica em estado (identidade estavel): o modal reinicia o form
    // quando ele muda, e um objeto novo a cada render apagaria o que foi digitado.
    const openCreate = () => {
        setCreatePrefill({ domainId: selectedDomainId });
        setIsCreateOpen(true);
    };

    const openCreateForPost = (domainId: number, idPostWp: string | null) => {
        setCreatePrefill({ domainId, originPost: idPostWp });
        setIsCreateOpen(true);
    };

    /** "+" do destino: nova origem apontando para um destino que ja existe */
    const openAddOriginToDest = (domainId: number, destGroup: PostGroup | null, destSlug: string) => {
        setCreatePrefill({ domainId, destinationPost: destGroup?.id_post_wp ?? null, destinationSlug: destSlug });
        setIsCreateOpen(true);
    };

    const openEdit = (cascade: PriceCascade) => {
        setSelected(cascade);
        setIsEditOpen(true);
    };

    // ========== GROUPING LOGIC ==========

    const toggleDomainCollapse = (domainId: number) => {
        setCollapsedDomains(prev => {
            const next = new Set(prev);
            if (next.has(domainId)) next.delete(domainId);
            else next.add(domainId);
            return next;
        });
    };

    const togglePanel = (rowKey: string, panel: string) => {
        setExpandedPanels(prev => {
            const next = { ...prev };
            if (next[rowKey] === panel) delete next[rowKey];
            else next[rowKey] = panel;
            return next;
        });
    };

    /**
     * Agrupa os cascades de cada domínio por post (P1) e depois agrupa
     * os posts de origem que redirecionam pro mesmo destino (P2) numa linha só.
     * O post de destino não vira linha própria — ele aparece no painel
     * "Configs do Destino" da linha que aponta pra ele.
     */
    const domainGroups: DomainGroup[] = useMemo(() => {
        if (!cascades?.length) return [];

        const byDomain = new Map<number, PriceCascade[]>();
        for (const c of cascades) {
            const arr = byDomain.get(c.domain_id) || [];
            arr.push(c);
            byDomain.set(c.domain_id, arr);
        }

        return Array.from(byDomain.entries()).map(([domainId, items]) => {
            // Agrupa por post (P1)
            const postGroups = new Map<string, PostGroup>();
            for (const c of items) {
                const key = c.id_post_wp || '__global__';
                let g = postGroups.get(key);
                if (!g) {
                    g = { key, slug: c.slug, id_post_wp: c.id_post_wp, cascades: [] };
                    postGroups.set(key, g);
                }
                if (!g.slug && c.slug) g.slug = c.slug;
                g.cascades.push(c);
            }

            // Destino (P2) de cada post = redirect_url de algum cascade dele
            const destOf = new Map<string, string>();
            for (const [key, g] of postGroups) {
                const withRedirect = g.cascades.find(c => c.redirect_url);
                if (withRedirect) {
                    const d = normalizeSlug(withRedirect.redirect_url);
                    if (d) destOf.set(key, d);
                }
            }

            // Index: slug normalizada -> postKey
            const byNormSlug = new Map<string, string>();
            for (const [key, g] of postGroups) {
                const ns = normalizeSlug(g.slug);
                if (ns) byNormSlug.set(ns, key);
            }

            // Origens agrupadas por destino
            const originsByDest = new Map<string, PostGroup[]>();
            for (const [key, dest] of destOf) {
                if (byNormSlug.get(dest) === key) continue; // auto-redirect
                const arr = originsByDest.get(dest) || [];
                arr.push(postGroups.get(key)!);
                originsByDest.set(dest, arr);
            }

            // Posts que são destino de alguém não viram linha própria
            const destPostKeys = new Set<string>();
            for (const dest of originsByDest.keys()) {
                const k = byNormSlug.get(dest);
                if (k) destPostKeys.add(k);
            }

            const rows: CascadeRow[] = [];
            const emittedDest = new Set<string>();
            for (const [key, g] of postGroups) {
                const dest = destOf.get(key);
                if (dest && byNormSlug.get(dest) !== key) {
                    if (!emittedDest.has(dest)) {
                        emittedDest.add(dest);
                        const destKey = byNormSlug.get(dest);
                        rows.push({
                            key: `dest:${dest}`,
                            origins: originsByDest.get(dest)!,
                            destSlug: dest,
                            destGroup: destKey ? postGroups.get(destKey)! : null,
                        });
                    }
                } else if (!destPostKeys.has(key)) {
                    rows.push({ key: `post:${key}`, origins: [g], destSlug: null, destGroup: null });
                }
            }

            return { domainId, domain: getDomainName(domainId), cascades: items, rows, postGroups };
        });
    }, [cascades, domains]);

    // ========== COMPACT RENDERERS (linhas agrupadas) ==========

    const renderPostPill = ({ slug, tone, active, count, onTogglePanel, onOpenModal }: {
        slug: string | null;
        tone: 'origin' | 'dest';
        active: boolean;
        count?: number;
        onTogglePanel: () => void;
        onOpenModal: () => void;
    }) => {
        const isDest = tone === 'dest';
        return (
            <div
                className={`inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-lg text-sm font-semibold cursor-pointer transition-colors max-w-full ${
                    isDest
                        ? active ? 'bg-amber-200/80 text-amber-900' : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                        : active ? 'bg-indigo-100 text-indigo-800' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                }`}
                onClick={(e) => { e.stopPropagation(); onTogglePanel(); }}
                title={isDest ? 'Ver configs do destino' : 'Ver/editar regras deste post'}
            >
                <span className="min-w-0 break-words leading-snug">{slug ? `/${slug}` : 'global'}</span>
                {typeof count === 'number' && count > 0 && (
                    <span className="text-[10px] font-normal opacity-70 shrink-0">{count}</span>
                )}
                <button
                    type="button"
                    className={`p-0.5 rounded-full hover:bg-white/80 shrink-0 ${isDest ? 'text-amber-500' : 'text-pink-500'}`}
                    onClick={(e) => { e.stopPropagation(); onOpenModal(); }}
                    title="Editar via modal"
                >
                    <CircleDot className="h-4 w-4" />
                </button>
            </div>
        );
    };

    const renderAdUnitChips = (origins: PostGroup[]) => {
        // Um chip por ad unit. O representante e sempre uma cascata ativa quando
        // existe — chip apagado significa que nenhuma daquela ad unit esta valendo.
        const seen = new Map<string, PriceCascade>();
        for (const o of origins) {
            for (const c of o.cascades) {
                const current = seen.get(c.ad_unit);
                if (!current || (!isActive(current) && isActive(c))) seen.set(c.ad_unit, c);
            }
        }
        return Array.from(seen.values()).map(c => {
            const on = isActive(c);
            return (
                <span
                    key={c.ad_unit}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs whitespace-nowrap ${
                        on ? 'border-gray-200 bg-white text-gray-600' : 'border-dashed border-gray-300 bg-gray-50 text-gray-400'
                    }`}
                    title={on ? undefined : `${c.ad_unit} desativada — nao esta sendo aplicada`}
                >
                    <span className={`h-1.5 w-1.5 rounded-full ${on ? adUnitDot(c.ad_unit) : 'bg-gray-300'}`} />
                    {c.ad_unit} ({stepsSummary(stepsOf(c))})
                    {!on && <span className="text-[10px] font-medium">· inativa</span>}
                </span>
            );
        });
    };

    const renderCompactHeader = (tone: 'gray' | 'amber' = 'gray') => (
        <div className={`${COMPACT_GRID} px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider ${tone === 'amber' ? 'text-amber-600/80' : 'text-gray-400'}`}>
            <span>Ad Unit</span>
            <span>Step</span>
            <span>Granulacao</span>
            <span>Blocos</span>
            <span>Timeout</span>
            <span>Status</span>
            <span className="text-right">Acoes</span>
        </div>
    );

    const renderCompactRow = (c: PriceCascade, tone: 'gray' | 'amber' = 'gray') => {
        const on = isActive(c);
        const toggling = isToggling(c.id);
        return (
        <div key={c.id} className={`${COMPACT_GRID} px-3 py-2 border rounded-lg ${
            !on ? 'bg-gray-50 border-dashed border-gray-300' : tone === 'amber' ? 'bg-white border-amber-200' : 'bg-white border-gray-200'
        }`}>
            <div className="flex items-center gap-2 min-w-0">
                <span className={`h-2 w-2 rounded-full shrink-0 ${on ? adUnitDot(c.ad_unit) : 'bg-gray-300'}`} />
                <span className={`text-sm font-medium truncate ${on ? 'text-gray-800' : 'text-gray-400 line-through decoration-gray-300'}`}>{c.ad_unit}</span>
                {c.country && c.country !== '*' && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">{c.country}</Badge>
                )}
            </div>
            <Select
                value={String(c.granulation_id ?? 'default')}
                onValueChange={(v) => quickUpdateMutation.mutate({
                    id: c.id,
                    data: { granulation_id: v === 'default' ? null : Number(v) },
                })}
            >
                <SelectTrigger className={`h-8 text-xs font-medium ${on
                    ? 'bg-purple-50/70 border-purple-200 text-purple-700 hover:bg-purple-100/70'
                    : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'
                }`}>
                    <SelectValue>{stepsSummary(stepsOf(c))}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="default">
                        Padrao ({cascadeStepsConfig?.[c.ad_unit]?.count ?? '—'} steps)
                    </SelectItem>
                    {granulations?.map(g => (
                        <SelectItem key={g.id} value={String(g.id)}>
                            {g.name} ({stepsSummary(g.steps)})
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <div className="min-w-0">
                <span className={`text-xs truncate block ${on ? 'text-gray-600' : 'text-gray-400'}`}>{c.granulation_name || 'Padrao'}</span>
                {c.granulation_name_p2 && (
                    <span className="text-[10px] text-gray-400 truncate block">P2: {c.granulation_name_p2}</span>
                )}
            </div>
            <span className={`text-xs truncate ${on ? 'text-gray-600' : 'text-gray-400'}`}>{c.block_mode === 'cascade_blocks' ? 'Cascade Blocks' : 'Padrao'}</span>
            <span className={`inline-flex items-center gap-1 text-xs ${on ? 'text-gray-500' : 'text-gray-400'}`}>
                <Timer className="h-3 w-3" />{(c.timeout / 1000).toFixed(1)}s
            </span>
            <div className="flex items-center gap-1.5">
                {toggling
                    ? <span className="inline-flex w-9 justify-center shrink-0"><Loader2 className="h-4 w-4 animate-spin text-purple-500" /></span>
                    : <Switch
                        checked={on}
                        onCheckedChange={(next) => toggleStatusMutation.mutate({
                            id: c.id,
                            status: next ? 'active' : 'inactive',
                            adUnit: c.ad_unit,
                        })}
                        disabled={toggleStatusMutation.isPending}
                        className="shrink-0 data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-gray-300"
                        aria-label={on ? `Desativar cascata ${c.ad_unit}` : `Ativar cascata ${c.ad_unit}`}
                        title={on
                            ? 'Desativar: para de ser aplicada, sem perder a configuracao'
                            : 'Ativar: volta a valer no site'}
                    />
                }
                <span className={`text-[10px] font-medium ${on ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {on ? 'Ativa' : 'Inativa'}
                </span>
            </div>
            <div className="flex items-center justify-end gap-0.5">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(c)}>
                    <Pencil className="h-3.5 w-3.5 text-gray-500" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setSelected(c); setIsDeleteOpen(true); }}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
            </div>
        </div>
        );
    };

    // ========== RENDER ==========

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-200">
                        <ArrowDownUp className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Price Cascade</h1>
                        <p className="text-sm text-gray-500">Waterfall bidding - steps fixos por ad unit</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setIsGranulationListOpen(true)}
                    >
                        <Layers className="h-4 w-4 mr-2" />
                        Granulacoes
                        {granulations && granulations.length > 0 && (
                            <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">{granulations.length}</Badge>
                        )}
                    </Button>
                    <Button onClick={openCreate} className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-md">
                        <Plus className="h-4 w-4 mr-2" />
                        Novo Cascade
                    </Button>
                </div>
            </div>

            {/* Domain Selector */}
            <div className="relative">
                <button
                    onClick={() => setDomainOpen(!domainOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        {selectedDomain ? (
                            <>
                                <img src={getFaviconUrl(selectedDomain.domain)} className="h-5 w-5 rounded" alt="" />
                                <span className="font-medium">{selectedDomain.domain}</span>
                            </>
                        ) : (
                            <span className="text-gray-500">Todos os domínios</span>
                        )}
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${domainOpen ? 'rotate-180' : ''}`} />
                </button>

                {domainOpen && (
                    <>
                        <div className="fixed inset-0 z-10" onClick={() => { setDomainOpen(false); setDomainSearch(''); }} />
                        <div className="absolute z-20 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                            <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar domínio..."
                                        value={domainSearch}
                                        onChange={(e) => setDomainSearch(e.target.value)}
                                        className="w-full h-10 pl-10 pr-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="max-h-[240px] overflow-y-auto">
                                <button
                                    onClick={() => { setSelectedDomainId(null); setDomainOpen(false); setDomainSearch(''); }}
                                    className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Layers className="h-4 w-4 text-gray-400" />
                                    Todos os domínios
                                    {!selectedDomainId && <Check className="h-4 w-4 ml-auto text-purple-600" />}
                                </button>
                                {filteredDomains.map((d: Domain) => (
                                    <button
                                        key={d.id}
                                        onClick={() => { setSelectedDomainId(d.id); setDomainOpen(false); setDomainSearch(''); }}
                                        className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <img src={getFaviconUrl(d.domain)} className="h-4 w-4 rounded" alt="" />
                                        {d.domain}
                                        {selectedDomainId === d.id && <Check className="h-4 w-4 ml-auto text-purple-600" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                    <p className="mt-3 text-sm text-gray-500">Carregando cascades...</p>
                </div>
            ) : !cascades?.length ? (
                <div className="text-center py-20 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <ArrowDownUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium text-gray-700">Nenhum cascade encontrado</p>
                    <p className="text-sm text-gray-400 mt-1">Crie um novo cascade para começar o waterfall bidding</p>
                    <Button onClick={openCreate} variant="outline" className="mt-4">
                        <Plus className="h-4 w-4 mr-2" /> Criar cascade
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    {domainGroups.map((group) => {
                        const isCollapsed = collapsedDomains.has(group.domainId);
                        const domainOriginCascades = group.rows.flatMap(r => r.origins.flatMap(o => o.cascades));
                        const domainDestCascades = group.rows.flatMap(r => r.destGroup ? r.destGroup.cascades : []);
                        const inactiveCount = group.cascades.filter(c => !isActive(c)).length;

                        return (
                            <div key={group.domainId} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                {/* Domain header */}
                                <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                                    <button
                                        onClick={() => toggleDomainCollapse(group.domainId)}
                                        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                                    >
                                        {isCollapsed
                                            ? <ChevronRight className="h-4 w-4 text-gray-400" />
                                            : <ChevronDown className="h-4 w-4 text-gray-400" />
                                        }
                                        <img src={getFaviconUrl(group.domain)} className="h-5 w-5 rounded" alt="" />
                                        <span className="font-semibold text-gray-800">{group.domain}</span>
                                        <Badge variant="outline" className="text-xs font-normal">
                                            {group.cascades.length} cascade{group.cascades.length !== 1 ? 's' : ''}
                                        </Badge>
                                        {inactiveCount > 0 && (
                                            <Badge variant="outline" className="text-xs font-normal border-dashed border-gray-300 bg-gray-50 text-gray-500">
                                                {inactiveCount} inativa{inactiveCount !== 1 ? 's' : ''}
                                            </Badge>
                                        )}
                                    </button>
                                    {!isCollapsed && (
                                        <BulkApplyBar
                                            scopes={[
                                                { label: 'Posts de origem', list: domainOriginCascades },
                                                { label: 'Posts de destino', list: domainDestCascades },
                                                { label: 'Site todo', list: group.cascades },
                                            ]}
                                            granulations={granulations}
                                            applying={bulkApplying}
                                            onApply={applyBulk}
                                        />
                                    )}
                                </div>

                                {/* Cascade rows (P1 -> P2) */}
                                {!isCollapsed && (
                                    <div className="p-3 space-y-2.5">
                                        {group.rows.map((row) => {
                                            const rowKey = `${group.domainId}:${row.key}`;
                                            const panel = expandedPanels[rowKey];
                                            const isOpen = !!panel;
                                            const originsToShow = !panel || panel === 'all'
                                                ? row.origins
                                                : row.origins.filter(o => `post:${o.key}` === panel);

                                            return (
                                                <div key={row.key} className={`border rounded-xl overflow-hidden transition-colors ${isOpen ? 'border-gray-300 shadow-sm' : 'border-gray-200'}`}>
                                                    {/* Header da linha: origens -> destino + chips */}
                                                    <div
                                                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50/60 flex-wrap"
                                                        onClick={() => togglePanel(rowKey, 'all')}
                                                    >
                                                        {isOpen
                                                            ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                                                            : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                                                        }
                                                        <div className="flex flex-col gap-1.5 items-start shrink-0 max-w-[340px]">
                                                            {row.origins.map(o => (
                                                                <div key={o.key} className="max-w-full">
                                                                    {renderPostPill({
                                                                        slug: o.slug,
                                                                        tone: 'origin',
                                                                        active: panel === `post:${o.key}` || panel === 'all',
                                                                        onTogglePanel: () => togglePanel(rowKey, row.origins.length > 1 ? `post:${o.key}` : 'all'),
                                                                        onOpenModal: () => setPostModal({ domainId: group.domainId, postKey: o.key, slug: o.slug }),
                                                                    })}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {row.destSlug && (
                                                            <>
                                                                <ArrowRight className="h-4 w-4 text-gray-300 shrink-0" />
                                                                <div className="max-w-[320px] shrink-0">
                                                                    {renderPostPill({
                                                                        slug: row.destSlug,
                                                                        tone: 'dest',
                                                                        active: panel === 'dest',
                                                                        count: row.destGroup?.cascades.length,
                                                                        onTogglePanel: () => togglePanel(rowKey, 'dest'),
                                                                        onOpenModal: () => setPostModal({
                                                                            domainId: group.domainId,
                                                                            postKey: row.destGroup?.key ?? null,
                                                                            slug: row.destSlug,
                                                                        }),
                                                                    })}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-amber-200 bg-white text-amber-600 hover:bg-amber-50 shrink-0"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openAddOriginToDest(group.domainId, row.destGroup, row.destSlug!);
                                                                    }}
                                                                    title="Adicionar outro post de origem para este destino"
                                                                    aria-label="Adicionar outro post de origem para este destino"
                                                                >
                                                                    <Plus className="h-4 w-4" />
                                                                </button>
                                                            </>
                                                        )}
                                                        <div className="ml-auto flex-1 min-w-[260px] flex items-center gap-1.5 flex-wrap justify-end pl-2">
                                                            {renderAdUnitChips(row.origins)}
                                                        </div>
                                                    </div>

                                                    {/* Painel: regras das origens (P1) */}
                                                    {panel && panel !== 'dest' && (
                                                        <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-3 space-y-3 overflow-x-auto">
                                                            <div className="flex items-center justify-between gap-2 flex-wrap min-w-[680px]">
                                                                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                                                    {originsToShow.length === 1
                                                                        ? `Regras — ${originsToShow[0].slug ? `/${originsToShow[0].slug}` : 'global'}`
                                                                        : 'Regras das origens'}
                                                                </span>
                                                                {row.origins.length > 1 && (
                                                                    <BulkApplyBar
                                                                        list={row.origins.flatMap(o => o.cascades)}
                                                                        label="Aplicar a todas P1 deste grupo:"
                                                                        granulations={granulations}
                                                                        applying={bulkApplying}
                                                                        onApply={applyBulk}
                                                                    />
                                                                )}
                                                            </div>
                                                            {originsToShow.map(o => (
                                                                <div key={o.key} className="space-y-1.5 min-w-[680px]">
                                                                    {originsToShow.length > 1 && (
                                                                        <div className="text-xs font-semibold text-indigo-600 px-1">
                                                                            {o.slug ? `/${o.slug}` : 'global'}
                                                                        </div>
                                                                    )}
                                                                    {renderCompactHeader()}
                                                                    {o.cascades.map(c => renderCompactRow(c))}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Painel: configs do destino (P2) */}
                                                    {panel === 'dest' && row.destSlug && (
                                                        <div className="border-t border-amber-200 bg-amber-50/60 px-3 py-3 space-y-2 overflow-x-auto">
                                                            <div className="flex items-center justify-between gap-2 flex-wrap min-w-[680px]">
                                                                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                                                                    Configs do destino — /{row.destSlug}/
                                                                </span>
                                                            </div>
                                                            {row.destGroup ? (
                                                                <div className="space-y-1.5 min-w-[680px]">
                                                                    {renderCompactHeader('amber')}
                                                                    {row.destGroup.cascades.map(c => renderCompactRow(c, 'amber'))}
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center justify-between py-2 min-w-[680px]">
                                                                    <span className="text-sm text-amber-700/70">Nenhum cascade registrado no destino</span>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                                                                        onClick={() => {
                                                                            setCreatePrefill({ domainId: group.domainId, destinationSlug: row.destSlug, destinationOnly: true });
                                                                            setIsCreateOpen(true);
                                                                        }}
                                                                    >
                                                                        <Plus className="h-3.5 w-3.5 mr-1.5" /> Criar cascade
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create Dialog — varias origens -> um destino, em lote */}
            <CreateCascadeModal
                open={isCreateOpen}
                onOpenChange={setIsCreateOpen}
                prefill={createPrefill}
                domains={domains}
                granulations={granulations}
                cascadeStepsConfig={cascadeStepsConfig}
            />

            {/* Edit Dialog — mesmo visual da criacao, edita uma cascata */}
            <EditCascadeModal
                open={isEditOpen}
                onOpenChange={setIsEditOpen}
                cascade={selected}
                domains={domains}
                granulations={granulations}
                cascadeStepsConfig={cascadeStepsConfig}
            />

            {/* Delete Dialog */}
            <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <Trash2 className="h-5 w-5" />
                            Remover Cascade
                        </DialogTitle>
                        <DialogDescription>
                            Tem certeza que deseja remover o cascade para <strong>{selected?.ad_unit}</strong> / <strong>{selected?.country}</strong>
                            {selected?.id_post_wp && <> (Post #{selected.id_post_wp})</>}?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancelar</Button>
                        <Button
                            variant="destructive"
                            onClick={() => selected && deleteMutation.mutate(selected.id)}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Remover
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Post Configs Modal (bolinha) */}
            <Dialog open={!!postModal} onOpenChange={(open) => { if (!open) setPostModal(null); }}>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <div className="p-1.5 rounded-lg bg-indigo-100">
                                <CircleDot className="h-4 w-4 text-indigo-600" />
                            </div>
                            <span className="truncate">{postModal?.slug ? `/${postModal.slug}` : 'Global'}</span>
                        </DialogTitle>
                        <DialogDescription>
                            Regras de cascade deste post — mude o step direto no dropdown ou abra a edicao completa no lapis
                        </DialogDescription>
                    </DialogHeader>
                    {(() => {
                        if (!postModal) return null;
                        const dg = domainGroups.find(g => g.domainId === postModal.domainId);
                        const pg = postModal.postKey ? dg?.postGroups.get(postModal.postKey) : null;
                        const list = pg?.cascades || [];
                        return (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                                        {dg && <img src={getFaviconUrl(dg.domain)} className="h-3.5 w-3.5 rounded" alt="" />}
                                        {dg?.domain}
                                    </span>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => openCreateForPost(postModal.domainId, pg?.id_post_wp ?? null)}
                                        >
                                            <Plus className="h-3.5 w-3.5 mr-1.5" /> Novo cascade
                                        </Button>
                                    </div>
                                </div>
                                {list.length > 0 ? (
                                    <div className="space-y-1.5 overflow-x-auto">
                                        <div className="min-w-[680px] space-y-1.5">
                                            {renderCompactHeader()}
                                            {list.map(c => renderCompactRow(c))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-sm text-gray-400">
                                        Nenhum cascade registrado neste post
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Granulation List Dialog */}
            <Dialog open={isGranulationListOpen} onOpenChange={setIsGranulationListOpen}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <div className="p-1.5 rounded-lg bg-purple-100">
                                <Layers className="h-4 w-4 text-purple-600" />
                            </div>
                            Granulacoes
                        </DialogTitle>
                        <DialogDescription>
                            Presets de steps customizados para cascade
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Button
                            size="sm"
                            className="w-full"
                            variant="outline"
                            onClick={() => {
                                setEditingGranulation(null);
                                setGranulationForm({ name: '', steps: [] });
                                setIsGranulationListOpen(false);
                                setIsGranulationOpen(true);
                            }}
                        >
                            <Plus className="h-3.5 w-3.5 mr-1.5" /> Nova Granulacao
                        </Button>
                        {!granulations?.length ? (
                            <div className="text-center py-8 text-sm text-gray-400">
                                Nenhuma granulacao criada ainda
                            </div>
                        ) : (
                            <div className="grid gap-2">
                                {granulations.map(g => {
                                    const isExpanded = expandedGranulations.has(g.id);
                                    return (
                                        <div key={g.id} className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                                            <div className="flex items-center justify-between p-3">
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                                                    onClick={() => setExpandedGranulations(prev => {
                                                        const next = new Set(prev);
                                                        if (next.has(g.id)) next.delete(g.id); else next.add(g.id);
                                                        return next;
                                                    })}
                                                >
                                                    {isExpanded
                                                        ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                                        : <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                                    }
                                                    <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-xs shrink-0">
                                                        {g.name}
                                                    </Badge>
                                                    <span className="text-xs text-gray-500 shrink-0">{g.steps.length} steps</span>
                                                    {!isExpanded && (
                                                        <span className="text-xs text-gray-400 font-mono truncate">
                                                            ${g.steps[0]} → ${g.steps[g.steps.length - 1]}
                                                        </span>
                                                    )}
                                                </button>
                                                <div className="flex items-center gap-1 shrink-0 ml-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => {
                                                            setEditingGranulation(g);
                                                            setGranulationForm({ name: g.name, steps: [...g.steps] });
                                                            setIsGranulationListOpen(false);
                                                            setIsGranulationOpen(true);
                                                        }}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5 text-gray-500" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => {
                                                            if (confirm(`Remover granulacao "${g.name}"? Cascades vinculados voltarao ao padrao.`)) {
                                                                deleteGranulationMutation.mutate(g.id);
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                                    </Button>
                                                </div>
                                            </div>
                                            {isExpanded && (
                                                <div className="px-3 pb-3 pt-0">
                                                    <div className="flex flex-wrap gap-1">
                                                        {g.steps.map((s, i) => (
                                                            <span key={i} className="text-[10px] font-mono text-purple-600 bg-white px-1.5 py-0.5 rounded border border-purple-100">
                                                                P{i + 1}: ${s}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Granulation Create/Edit Dialog */}
            <Dialog open={isGranulationOpen} onOpenChange={(open) => { setIsGranulationOpen(open); if (!open) setStepsPopoverOpen(false); }}>
                <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <div className="p-1.5 rounded-lg bg-purple-100">
                                <ArrowDownUp className="h-4 w-4 text-purple-600" />
                            </div>
                            {editingGranulation ? 'Editar Granulacao' : 'Nova Granulacao'}
                        </DialogTitle>
                        <DialogDescription>
                            Defina um preset de steps customizados para cascade
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">Nome</Label>
                            <Input
                                placeholder="Ex: Agressivo, Conservador..."
                                value={granulationForm.name}
                                onChange={(e) => setGranulationForm(f => ({ ...f, name: e.target.value }))}
                            />
                        </div>

                        {/* Selected steps summary */}
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium text-gray-700">
                                Steps (precos)
                            </Label>
                            <div className="flex items-center gap-2">
                                {granulationForm.steps.length > 0 && (
                                    <span className="text-xs text-gray-500 font-mono">
                                        {granulationForm.steps.length} steps &middot; ${granulationForm.steps[0]} → ${granulationForm.steps[granulationForm.steps.length - 1]}
                                    </span>
                                )}
                                <div className="flex gap-1.5">
                                    <button
                                        type="button"
                                        className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                                        onClick={() => {
                                            if (availablePrices.length > 0) {
                                                setGranulationForm(f => ({ ...f, steps: [...availablePrices] }));
                                            }
                                        }}
                                    >
                                        Todos
                                    </button>
                                    <span className="text-gray-300">|</span>
                                    <button
                                        type="button"
                                        className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                                        onClick={() => setGranulationForm(f => ({ ...f, steps: [] }))}
                                    >
                                        Limpar
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Price list with search */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col flex-1 min-h-0">
                            <div className="p-2 border-b border-gray-100 shrink-0">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar preco..."
                                        value={stepsPopoverOpen ? '' : ''}
                                        onChange={(e) => {
                                            const el = e.target;
                                            // Filter is handled via data attribute
                                            const container = el.closest('.space-y-4')?.querySelector('[data-price-list]');
                                            if (container) {
                                                const items = container.querySelectorAll('[data-price-value]');
                                                const query = el.value.toLowerCase();
                                                items.forEach((item: Element) => {
                                                    const val = item.getAttribute('data-price-value') || '';
                                                    (item as HTMLElement).style.display = val.includes(query) ? '' : 'none';
                                                });
                                            }
                                        }}
                                        className="w-full h-8 pl-8 pr-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-purple-500"
                                    />
                                </div>
                            </div>
                            <div data-price-list className="overflow-y-auto flex-1" style={{ maxHeight: '280px' }}>
                                {availablePrices.map((price) => {
                                    const isSelected = granulationForm.steps.includes(price);
                                    return (
                                        <label
                                            key={price}
                                            data-price-value={price.toFixed(2)}
                                            className={`flex items-center gap-3 px-3 py-1.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                                                isSelected ? 'bg-purple-50/60' : ''
                                            }`}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={(checked) => {
                                                    setGranulationForm(f => {
                                                        const newSteps = checked
                                                            ? [...f.steps, price].sort((a, b) => b - a)
                                                            : f.steps.filter(s => s !== price);
                                                        return { ...f, steps: newSteps };
                                                    });
                                                }}
                                                className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                                            />
                                            <span className={`font-mono text-sm ${isSelected ? 'text-purple-700 font-medium' : 'text-gray-700'}`}>
                                                ${price.toFixed(2)}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Selected steps preview */}
                        {granulationForm.steps.length > 0 && (
                            <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto shrink-0">
                                {granulationForm.steps.map((s, i) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setGranulationForm(f => ({
                                            ...f,
                                            steps: f.steps.filter(x => x !== s)
                                        }))}
                                        className="inline-flex items-center gap-1 text-[10px] font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100 hover:bg-purple-100 hover:border-purple-300 transition-colors group"
                                        title="Clique para remover"
                                    >
                                        P{i + 1}: ${s}
                                        <span className="text-purple-400 group-hover:text-red-500">&times;</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 shrink-0">
                            <Button variant="outline" onClick={() => setIsGranulationOpen(false)}>Cancelar</Button>
                            <Button
                                onClick={() => {
                                    if (!granulationForm.name.trim() || granulationForm.steps.length === 0) {
                                        toast({ title: 'Preencha nome e selecione ao menos 1 preco', variant: 'destructive' });
                                        return;
                                    }
                                    const payload = { name: granulationForm.name.trim(), steps: granulationForm.steps };
                                    if (editingGranulation) {
                                        updateGranulationMutation.mutate({ id: editingGranulation.id, data: payload });
                                    } else {
                                        createGranulationMutation.mutate(payload);
                                    }
                                }}
                                disabled={createGranulationMutation.isPending || updateGranulationMutation.isPending}
                            >
                                {(createGranulationMutation.isPending || updateGranulationMutation.isPending) && (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                )}
                                Salvar
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
